import { portalPageMeta } from "layouts/Admin2/pageMeta";
import PageHeading from "admin/components/PageHeading";
import useFetchAuditTrail from "api/AuditTrail/useFetchAuditTrail";
import Admin2 from "layouts/Admin2";
import moment from "moment";
import { Column } from "primereact/column";
import { DataTable } from "components/PortalDataTable";
import Calendar from "components/PortalDatePicker";
import { useState } from "react";
import { CustomPaginator } from "components";
import AdminPageLoader from "components/AdminPageLoader";
import ChangeDetail from "./ChangeDetail";
import useAuditTrailFilters from "./useAuditTrailFilters";

const DOMAIN_OPTIONS = [
	{ label: "All domains", value: "" },
	{ label: "IAM", value: "iam" },
	{ label: "Books", value: "books" },
	{ label: "Orders", value: "orders" },
	{ label: "Payments", value: "payments" },
	{ label: "Publishers", value: "publishers" },
	{ label: "Institutions", value: "institutions" },
	{ label: "Subscriptions", value: "subscriptions" },
	{ label: "Vouchers", value: "vouchers" },
	{ label: "System", value: "system" },
];

const AuditTrail = () => {
	const [expandedRows, setExpandedRows] = useState(null);

	const {
		page,
		rowsPerPage,
		firstPage,
		fromDate,
		toDate,
		searchInput,
		eventInput,
		search,
		domain,
		event,
		changePage,
		changeRowsPerPage,
		changeFirstPage,
		changeFromDate,
		changeToDate,
		changeSearchInput,
		changeEventInput,
		changeDomain,
		clearFilters,
	} = useAuditTrailFilters();

	const { data, isLoading, isFetching } = useFetchAuditTrail({
		page,
		rows_per_page: rowsPerPage,
		from_date: fromDate,
		to_date: toDate,
		search,
		domain,
		event,
	});

	const records = data?.data?.records;
	const countSetFilters = [fromDate, toDate, search, domain, event].filter((item) => item)?.length;

	const actorTemplate = (row) => {
		if (!row?.causer) {
			return <span className="text-gray-500">System</span>;
		}
		return (
			<div>
				<div>{row.causer.name || row.causer.email}</div>
				<div className="text-xs text-gray-500">{row.causer.email}</div>
			</div>
		);
	};

	const subjectTemplate = (row) => {
		if (!row?.subject) {
			return "—";
		}
		return (
			<div>
				<div>{row.subject.type}</div>
				<div className="text-xs text-gray-500">{row.subject.label || row.subject.id}</div>
			</div>
		);
	};

	if (isLoading && !data) {
		return <AdminPageLoader />;
	}

	return (
		<Admin2>
			<div className="w-full flex-1 flex flex-col justify-between gap-4">
				<div className="portal-page-container">
					<PageHeading title="Audit trail" description={portalPageMeta("/audit-trail").description} />

					<div className="portal-filter-bar">
						<input
							type="text"
							value={searchInput}
							onChange={(e) => changeSearchInput(e.target.value)}
							placeholder="Search actor, event, message" aria-label="Search audit trail"
							className="border border-gray-300 bg-white rounded h-10 px-3 w-56"
						/>

						<input
							type="text"
							value={eventInput}
							onChange={(e) => changeEventInput(e.target.value)}
							placeholder="Event filter" aria-label="Filter audit events"
							className="border border-gray-300 bg-white rounded h-10 px-3 w-40"
						/>

						{/* <select
							value={domain}
							onChange={(e) => changeDomain(e.target.value)}
							className="border border-gray-300 bg-white rounded h-10 px-2"
						>
							{DOMAIN_OPTIONS.map((option) => (
								<option key={option.value || "all"} value={option.value}>
									{option.label}
								</option>
							))}
						</select> */}

						<select
							value={rowsPerPage}
							onChange={(e) => changeRowsPerPage(e.target.value)}
							className="border border-gray-300 bg-white rounded h-10 px-2"
						>
							<option value="20">20</option>
							<option value="40">40</option>
							<option value="60">60</option>
							<option value="80">80</option>
							<option value="100">100</option>
						</select>

						{fromDate && (
							<Calendar
								value={new Date(fromDate)}
								name="from_date"
								dateFormat="yy-mm-dd"
								placeholder="Date (from)"
								className="w-52 h-10 font-sans"
								inputClassName="border border-gray-300 bg-white rounded"
								showIcon
								readOnlyInput
								showButtonBar
								onChange={(e) => changeFromDate(moment(e.target.value).format("YYYY-MM-DD"))}
								onClearButtonClick={() => changeFromDate("")}
							/>
						)}

						{!fromDate && (
							<Calendar
								value={null}
								name="from_date"
								dateFormat="yy-mm-dd"
								placeholder="Date (from)"
								className="w-52 h-10 font-sans"
								inputClassName="border border-gray-300 bg-white rounded"
								showIcon
								readOnlyInput
								showButtonBar
								onChange={(e) => changeFromDate(moment(e.target.value).format("YYYY-MM-DD"))}
							/>
						)}

						{toDate && (
							<Calendar
								value={new Date(toDate)}
								name="to_date"
								dateFormat="yy-mm-dd"
								placeholder="Date (to)"
								className="w-52 h-10 font-sans"
								inputClassName="border border-gray-300 bg-white rounded"
								showIcon
								showButtonBar
								onChange={(e) => changeToDate(moment(e.target.value).format("YYYY-MM-DD"))}
								onClearButtonClick={() => changeToDate("")}
							/>
						)}

						{!toDate && (
							<Calendar
								value={null}
								name="to_date"
								dateFormat="yy-mm-dd"
								placeholder="Date (to)"
								className="w-52 h-10 font-sans"
								inputClassName="border border-gray-300 bg-white rounded"
								showIcon
								showButtonBar
								onChange={(e) => changeToDate(moment(e.target.value).format("YYYY-MM-DD"))}
							/>
						)}

						{countSetFilters > 0 && (
							<button
								type="button"
								onClick={clearFilters}
								className="border border-gray-300 bg-white rounded h-10 px-3"
							>
								Clear filters ({countSetFilters})
							</button>
						)}
					</div>
				</div>

				<DataTable
					value={records}
					dataKey="id"
					autoLayout
					expandedRows={expandedRows}
					onRowToggle={(e) => setExpandedRows(e.data)}
					rowExpansionTemplate={(row) => <ChangeDetail row={row} />}
					tableClassName={`font-sans text-sm${isFetching && !isLoading ? " opacity-60" : ""}`}
				>
					<Column expander style={{ width: "3em" }} />
					<Column
						header="When"
						body={(row) => <span>{moment(row?.created_at).format("LLL")}</span>}
					/>
					<Column header="Actor" body={actorTemplate} />
					<Column field="event" header="Event" />
					<Column field="domain" header="Domain" />
					<Column header="Target" body={subjectTemplate} />
					<Column field="message" header="Summary" />
				</DataTable>

				<div className="flex-1 flex justify-end">
					<CustomPaginator
						firstPage={firstPage}
						setFirstPage={changeFirstPage}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={changeRowsPerPage}
						totalRecords={data?.data?.total_records}
						setPage={changePage}
						showRowsPerPageDropdown={false}
					/>
				</div>
			</div>
		</Admin2>
	);
};

export default AuditTrail;
